import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ScrollText, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function AuditAdmin() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/audit", { params: { limit: 200 } });
    setItems(r.data.items); setTotal(r.data.total);
  };
  useEffect(() => { load(); }, []);

  const doVerify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/audit/verify-chain");
      setVerify(r.data);
      toast[r.data.ok ? "success" : "error"](r.data.ok ? `Chain OK · ${r.data.total_entries} entries` : `Chain BROKEN at #${r.data.broken_at}`);
    } finally { setBusy(false); }
  };

  return (
    <AppShell
      title="Audit Ledger"
      subtitle={`${total} entries · hash-chained · tenant-scoped`}
      actions={
        <div className="flex items-center gap-3">
          {verify && (
            <StatusChip
              status={verify.ok ? "verified" : "error"}
              label={verify.ok ? `Chain OK (${verify.total_entries})` : `BROKEN #${verify.broken_at}`}
              testId="audit-verify-result"
            />
          )}
          <Button onClick={doVerify} disabled={busy} className="bg-erf-dark text-erf-cream" data-testid="audit-verify-btn">
            {verify?.ok === false ? <ShieldAlert className="w-4 h-4 mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
            {busy ? "Verifying…" : "Verify chain"}
          </Button>
        </div>
      }
    >
      {items.length === 0 ? (
        <EmptyState icon={ScrollText} title="No entries yet" subtitle="Every state-changing API call appends here." />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2 mono">#</th>
                <th className="text-left px-3 py-2">TS (UTC)</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Resource</th>
                <th className="text-left px-3 py-2 mono">prev_hash</th>
                <th className="text-left px-3 py-2 mono">entry_hash</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-erf-borderCream/60" data-testid={`audit-row-${e.hash_chain_index}`}>
                  <td className="px-3 py-2 mono">{e.hash_chain_index}</td>
                  <td className="px-3 py-2 mono text-[11px]">{(e.ts||"").replace("T"," ").slice(0,19)}</td>
                  <td className="px-3 py-2 mono text-[11px]">{e.actor_role}{e.on_behalf_of_user_id ? " (view-as)" : ""}</td>
                  <td className="px-3 py-2 font-nav">{e.action}</td>
                  <td className="px-3 py-2 mono text-[11px]">{e.resource_type}{e.resource_id ? "/" + String(e.resource_id).slice(0,8) : ""}</td>
                  <td className="px-3 py-2 mono text-[10px] text-erf-dark/50">{(e.prev_hash||"—").slice(0,10)}</td>
                  <td className="px-3 py-2 mono text-[10px] text-erf-dark/70">{(e.entry_hash||"").slice(0,10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
